// Shared between the embed player and the dashboard/API.

export type AutoplayMode = "smart" | "click";
export type EndAction = "none" | "replay";
export type PlayButtonShape = "circle" | "rounded" | "square";

export interface PlayButtonSettings {
  shape: PlayButtonShape;
  /** Diameter in px. */
  size: number;
  color: string;
  iconColor: string;
  pulse: boolean;
  /** Caption under the button. Empty = no caption. */
  text: string;
  textColor: string;
  textSize: number;
}

export interface UnmuteSettings {
  title: string;
  subtitle: string;
  bg: string;
  textColor: string;
  pulse: boolean;
}

export interface PlayerSettings {
  color: string;
  aspectRatio: string; // "auto" (from the video) | "16:9" | "9:16" | "3:4" ...
  radius: number;
  /**
   * smart: muted autoplay + "turn on sound" card;
   * click: play button over the muted preview loop.
   */
  autoplay: AutoplayMode;
  /** Seconds of the muted background loop shown until the viewer presses play. */
  previewLoop: number;
  playButton: PlayButtonSettings;
  unmute: UnmuteSettings;
  /** Cap for HLS quality (height), e.g. 720. 0 = no cap. */
  maxQuality: number;
  fakeProgress: boolean;
  /** 0..1 — how much faster the bar moves at the beginning. */
  fakeProgressStrength: number;
  progressHeight: number;
  allowPause: boolean;
  allowSeek: boolean;
  showTime: boolean;
  resume: boolean;
  resumeTitle: string;
  resumeContinue: string;
  resumeRestart: string;
  /** Pitch time: seconds when page elements with class .vsl-hide become visible. 0 = disabled. */
  revealAt: number;
  pixels: {
    enabled: boolean;
    prefix: string;
  };
  endAction: EndAction;
}

export const DEFAULT_SETTINGS: PlayerSettings = {
  color: "#e11d48",
  aspectRatio: "auto",
  radius: 12,
  autoplay: "smart",
  previewLoop: 15,
  playButton: {
    shape: "circle",
    size: 84,
    color: "#e11d48",
    iconColor: "#ffffff",
    pulse: true,
    text: "Click to watch",
    textColor: "#ffffff",
    textSize: 18,
  },
  unmute: {
    title: "Your video has already started",
    subtitle: "Click to listen",
    bg: "#4f86c6",
    textColor: "#ffffff",
    pulse: true,
  },
  maxQuality: 480,
  fakeProgress: true,
  fakeProgressStrength: 0.5,
  progressHeight: 6,
  allowPause: true,
  allowSeek: false,
  showTime: false,
  resume: true,
  resumeTitle: "You have already started watching this video",
  resumeContinue: "Continue watching",
  resumeRestart: "Watch from the beginning",
  revealAt: 0,
  pixels: { enabled: true, prefix: "VSL" },
  endAction: "none",
};

export function mergeSettings(partial: unknown): PlayerSettings {
  const p = (partial && typeof partial === "object" ? partial : {}) as Partial<PlayerSettings>;
  return {
    ...DEFAULT_SETTINGS,
    ...p,
    playButton: { ...DEFAULT_SETTINGS.playButton, ...(p.playButton ?? {}) },
    unmute: { ...DEFAULT_SETTINGS.unmute, ...(p.unmute ?? {}) },
    pixels: { ...DEFAULT_SETTINGS.pixels, ...(p.pixels ?? {}) },
  };
}

export interface VideoConfig {
  id: string;
  hls: string | null;
  mp4: string | null;
  /** Only shown when the browser blocks muted autoplay (e.g. iOS low-power mode). */
  poster: string | null;
  duration: number;
  width?: number;
  height?: number;
  /** Average bitrate of the best rendition the viewer may get, kbit/s (traffic estimate for native HLS). */
  kbps?: number;
}

/** "auto" -> the video's own proportions (falls back to 16:9 until they are known). */
export function effectiveAspect(aspect: string, width?: number, height?: number): string {
  if (aspect !== "auto") return aspect;
  return width && height ? `${width}:${height}` : "16:9";
}

export type BinomEvent = "play" | "pitch" | "p25" | "p50" | "p75" | "p95" | "offer";

/** Sends player milestones to the tracker as custom events (Binom event1..event30). */
export interface TrackerEvents {
  /** e.g. https://tracker.com/click?upd_clickid={clickid}&event{n}=1 — the browser calls the tracker itself */
  urlTemplate?: string;
  /** Our own endpoint; the server calls the tracker, so the page never shows its domain. */
  relayUrl?: string;
  /** milestone -> event number (0/absent = don't send) */
  events: Partial<Record<BinomEvent, number>>;
}

/** Milestones that can be mirrored to an ad platform's server API. */
export type CapiMilestone = "play" | "pitch" | "p25" | "p50" | "p75" | "p95" | "offer";

/**
 * Server-side conversion API. The player fires the browser pixel with the same
 * name and the same event id, which is what lets the platform drop duplicates.
 */
export interface ServerPixel {
  /** Which browser pixel to fire alongside the server copy. */
  platform: "meta" | "tiktok";
  /** Our endpoint that forwards the event from the server. */
  url: string;
  /** milestone -> platform event name; a missing entry is not sent. */
  events: Partial<Record<CapiMilestone, string>>;
}

/** Response of GET /api/p/:id */
export interface PublicPlayerConfig {
  id: string;
  /** Absolute URL of the tracking endpoint (bypasses the CDN). */
  trackUrl: string;
  settings: PlayerSettings;
  video: VideoConfig;
  tracker: TrackerEvents | null;
  /** Present only when the account turned the Conversions API on. */
  serverPixel?: ServerPixel | null;
}

/** Body of POST /api/t (short keys keep beacons small). */
export interface TrackPayload {
  s: string; // session id
  p: string; // player id
  v: string; // video id
  dur: number; // video duration, seconds
  sp: number; // start position (resume), seconds
  mp: number; // max position reached, seconds
  ws: number; // seconds actually watched
  ap: 0 | 1; // muted preview loop started (autoplay)
  st: 0 | 1; // started playing with sound
  um: 0 | 1; // unmuted / played with sound
  oc: 0 | 1; // clicked a link inside a pitch-time block
  kb: number; // video KB downloaded (hls.js) or estimated (native playback)
  ck?: string; // tracker click id (Binom clickid etc.)
  r?: string; // referrer
  tp?: number; // navigator.maxTouchPoints: tells an iPad (Mac User-Agent) from a Mac
  u?: Partial<Record<"source" | "medium" | "campaign" | "content" | "term", string>>;
  /** Ad-platform identifiers for server-side conversion APIs. */
  ad?: {
    fbp?: string;
    fbc?: string;
    ttp?: string;
    ttclid?: string;
    /** Page the player runs on, needed by Meta as event_source_url. */
    url?: string;
  };
}

/**
 * Fake progress: the bar jumps ahead at the start and slows down towards the end,
 * reaching 100% exactly when the video ends. f(x) = x^(1/p), p in [1, 3].
 * strength 0 = real progress. With p = 2.8 a 26-min video shows ~30% after the first minute.
 */
export function fakeExponent(strength: number): number {
  return 1 + 2 * Math.min(1, Math.max(0, strength));
}
export function toDisplayed(x: number, p: number): number {
  const c = Math.min(1, Math.max(0, x));
  return Math.pow(c, 1 / p);
}
export function toReal(f: number, p: number): number {
  const c = Math.min(1, Math.max(0, f));
  return Math.pow(c, p);
}
